import { i18n } from '@lingui/core'
import { t } from '@lingui/core/macro'
import type {
  DocumentMetadataUserEditValueKey,
  DocumentMetadataUserEditValues,
  DocumentPageMetadataUserEditValueKey,
  DocumentPageMetadataUserEditValues,
} from 'features/knowledgeDocumentViewer/types/documentMetadataUserEditSchemas'
import { metadataFieldLabelMessage } from 'features/knowledgeDocumentViewer/utils/metadataFieldLabelMessages'
import {
  buildDocumentMetadataUserEditValues,
  buildDocumentPageMetadataUserEditValues,
} from 'features/knowledgeDocumentViewer/utils/metadataUserEditWrite'
import { normalizeRevisionFamilyKeyPart } from 'features/knowledgeDocumentViewer/utils/pageRevisionFamily'

/** Shared page↔document fields. Cascade: single-page only; single-scope needs alignment. */

/** Missing/0 page count treated as 1 (same as the viewer). */
export function allowsCrossSurfaceMetadataCascade(
  totalPages: number | undefined
): boolean {
  const pages = totalPages ?? 0
  const effective = pages === 0 ? 1 : pages
  return effective === 1
}

/* eslint-disable lingui/no-unlocalized-strings */
export const SHARED_METADATA_FIELD_BRIDGES = [
  {
    sharedLabel: 'Drawing #',
    documentKey: 'drawing_number',
    pageKey: 'issuer_drawing_number',
    documentLabel: 'Drawing #',
    pageLabel: 'Drawing #',
  },
  {
    sharedLabel: 'System',
    documentKey: 'system',
    pageKey: 'system',
    documentLabel: 'System',
    pageLabel: 'System',
  },
  {
    sharedLabel: 'Vendor',
    documentKey: 'vendor_name',
    pageKey: 'issuer_company_name',
    documentLabel: 'Vendor',
    pageLabel: 'Vendor',
  },
  {
    sharedLabel: 'Revision',
    documentKey: 'revision_code',
    pageKey: 'latest_revision',
    documentLabel: 'Revision',
    pageLabel: 'Revision Code',
  },
  {
    sharedLabel: 'Rev. Date',
    documentKey: 'revision_date',
    pageKey: 'latest_revision_date',
    documentLabel: 'Rev. Date',
    pageLabel: 'Revision Date',
  },
] as const satisfies ReadonlyArray<{
  sharedLabel: string
  documentKey: DocumentMetadataUserEditValueKey
  pageKey: DocumentPageMetadataUserEditValueKey
  documentLabel: string
  pageLabel: string
}>
/* eslint-enable lingui/no-unlocalized-strings */

type SharedMetadataFieldBridge = (typeof SHARED_METADATA_FIELD_BRIDGES)[number]

function normalizeSharedMetadataValue(
  key: DocumentMetadataUserEditValueKey | DocumentPageMetadataUserEditValueKey,
  value: string | null | undefined
): string {
  if (key === 'drawing_number' || key === 'issuer_drawing_number') {
    return normalizeRevisionFamilyKeyPart(value)
  }
  return value?.trim() ?? ''
}

function sharedValuesAligned(
  key: DocumentMetadataUserEditValueKey | DocumentPageMetadataUserEditValueKey,
  left: string | null | undefined,
  right: string | null | undefined
): boolean {
  return (
    normalizeSharedMetadataValue(key, left) ===
    normalizeSharedMetadataValue(key, right)
  )
}

function sharedValueChanged(
  key: DocumentMetadataUserEditValueKey | DocumentPageMetadataUserEditValueKey,
  before: string | null | undefined,
  after: string | null | undefined
): boolean {
  return !sharedValuesAligned(key, before, after)
}

function bridgesCascadingFromPageEdit({
  pageBaseline,
  pageNext,
  documentBaseline,
  requireAlignment,
}: {
  pageBaseline: DocumentPageMetadataUserEditValues
  pageNext: DocumentPageMetadataUserEditValues
  documentBaseline: DocumentMetadataUserEditValues
  requireAlignment: boolean
}): SharedMetadataFieldBridge[] {
  return SHARED_METADATA_FIELD_BRIDGES.filter((bridge) => {
    const pageBefore = pageBaseline[bridge.pageKey]
    const pageAfter = pageNext[bridge.pageKey]
    if (!sharedValueChanged(bridge.pageKey, pageBefore, pageAfter)) {
      return false
    }
    if (!requireAlignment) return true
    return sharedValuesAligned(
      bridge.documentKey,
      documentBaseline[bridge.documentKey],
      pageBefore
    )
  })
}

function bridgesCascadingFromDocumentEdit({
  documentBaseline,
  documentNext,
  pageBaseline,
  requireAlignment,
}: {
  documentBaseline: DocumentMetadataUserEditValues
  documentNext: DocumentMetadataUserEditValues
  pageBaseline: DocumentPageMetadataUserEditValues
  requireAlignment: boolean
}): SharedMetadataFieldBridge[] {
  return SHARED_METADATA_FIELD_BRIDGES.filter((bridge) => {
    const documentBefore = documentBaseline[bridge.documentKey]
    const documentAfter = documentNext[bridge.documentKey]
    if (
      !sharedValueChanged(bridge.documentKey, documentBefore, documentAfter)
    ) {
      return false
    }
    if (!requireAlignment) return true
    return sharedValuesAligned(
      bridge.pageKey,
      pageBaseline[bridge.pageKey],
      documentBefore
    )
  })
}

/** Sparse document patch from a page edit. */
export function buildDocumentValuesCascadedFromPageEdit({
  pageBaseline,
  pageNext,
  documentBaseline,
  requireAlignment = true,
}: {
  pageBaseline: DocumentPageMetadataUserEditValues
  pageNext: DocumentPageMetadataUserEditValues
  documentBaseline: DocumentMetadataUserEditValues
  /** Family scope passes false to sync even when page↔doc drifted. */
  requireAlignment?: boolean
}): DocumentMetadataUserEditValues | undefined {
  const cascade: DocumentMetadataUserEditValues = {}
  for (const bridge of bridgesCascadingFromPageEdit({
    pageBaseline,
    pageNext,
    documentBaseline,
    requireAlignment,
  })) {
    cascade[bridge.documentKey] = pageNext[bridge.pageKey] ?? null
  }
  if (Object.keys(cascade).length === 0) return undefined
  return buildDocumentMetadataUserEditValues(cascade)
}

/** Sparse page patch from a document edit. */
export function buildPageValuesCascadedFromDocumentEdit({
  documentBaseline,
  documentNext,
  pageBaseline,
  requireAlignment = true,
}: {
  documentBaseline: DocumentMetadataUserEditValues
  documentNext: DocumentMetadataUserEditValues
  pageBaseline: DocumentPageMetadataUserEditValues
  /** Family scope passes false to sync even when page↔doc drifted. */
  requireAlignment?: boolean
}): DocumentPageMetadataUserEditValues | undefined {
  const cascade: DocumentPageMetadataUserEditValues = {}
  for (const bridge of bridgesCascadingFromDocumentEdit({
    documentBaseline,
    documentNext,
    pageBaseline,
    requireAlignment,
  })) {
    cascade[bridge.pageKey] = documentNext[bridge.documentKey] ?? null
  }
  if (Object.keys(cascade).length === 0) return undefined
  return buildDocumentPageMetadataUserEditValues(cascade)
}

/** Document Details labels that will cascade from a page draft. */
export function listDocumentLabelsCascadingFromPageEdit({
  pageBaseline,
  pageNext,
  documentBaseline,
  requireAlignment = true,
}: {
  pageBaseline: DocumentPageMetadataUserEditValues
  pageNext: DocumentPageMetadataUserEditValues
  documentBaseline: DocumentMetadataUserEditValues
  requireAlignment?: boolean
}): string[] {
  return bridgesCascadingFromPageEdit({
    pageBaseline,
    pageNext,
    documentBaseline,
    requireAlignment,
  }).map((bridge) => bridge.documentLabel)
}

/** Page Details labels that will cascade from a document draft. */
export function listPageLabelsCascadingFromDocumentEdit({
  documentBaseline,
  documentNext,
  pageBaseline,
  requireAlignment = true,
}: {
  documentBaseline: DocumentMetadataUserEditValues
  documentNext: DocumentMetadataUserEditValues
  pageBaseline: DocumentPageMetadataUserEditValues
  requireAlignment?: boolean
}): string[] {
  return bridgesCascadingFromDocumentEdit({
    documentBaseline,
    documentNext,
    pageBaseline,
    requireAlignment,
  }).map((bridge) => bridge.pageLabel)
}

export function formatCrossSurfaceCascadeWarning({
  counterpartSurface,
  labels,
}: {
  counterpartSurface: 'document' | 'page'
  labels: readonly string[]
}): string | undefined {
  if (labels.length === 0) return undefined
  const surface =
    counterpartSurface === 'document' ? t`Document Details` : t`Page Details`
  const names = labels.map((label) => i18n._(metadataFieldLabelMessage(label)))
  const leading = names.slice(0, -1).join(', ')
  const last = names[names.length - 1]
  const joined = names.length === 1 ? last : t`${leading}, and ${last}`
  return t`These changes also update ${surface}: ${joined}.`
}
